/* eslint-disable react/function-component-definition */

import React, { useEffect, useState, useRef, useCallback } from "react";

// 3rd party library
import { Feature } from "flagged";
import html2canvas from "html2canvas";
import { jsPDF as JSPDF } from "jspdf";
import moment from "moment";

// MUI Components
import { Card, Divider, Menu, MenuItem, Button, FormControl } from "@mui/material";
import pxToRem from "assets/theme/functions/pxToRem";

// Custom Componentsu
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageTitle from "examples/NewDesign/PageTitle";
import BasicButton from "examples/NewDesign/CustomButton/BasicButton";
import MDBox from "components/MDBox";
import ProjectTrackerTable from "examples/Tables/DataTable/projectTrackerTable";
// import FilterDropdown from "components/Dropdown/FilterDropdown";
import LocationCalculation from "examples/Drawers/Report/locationCalculation/index";
import MDTypography from "components/MDTypography";
import ResetFilterButton from "components/Buttons/ResetButton";
import CustomAutoComplete from "components/Dropdown/CustomAutoComeplete";
import FTextField from "components/Form/FTextField";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { openSnackbar } from "redux/Slice/Notification";
import { projectListThunk, exportProjectTrackerExcel } from "redux/Thunks/Filter";
import getProjectTrackerData from "redux/Thunks/ProjectTracker";
import { setStoreFilters, resetFilters } from "redux/Slice/Filter";

// Constants
import Constants, {
  PageTitles,
  Icons,
  Colors,
  FeatureTags,
  ButtonTitles,
  defaultData,
  FiltersModuleName,
} from "utils/Constants";
import reportCalculationData from "./data/reportCalculationData";

const projectTrackerFiltersArr = [
  {
    inputLabel: FiltersModuleName.PROJECT,
    list: [FiltersModuleName.PROJECT_TRACKER_FILTERS_TITLE_OBJ],
    selectedValue: FiltersModuleName.SELECT_IN_SMALL_CASE,
    textTransform: "none",
  },
  {
    inputLabel: FiltersModuleName.CREATED,
    type: "date",
    selectedValue: "",
  },
];

const ReportCalculation = () => {
  const dispatch = useDispatch();
  const projectTrackerRef = useRef();
  const projectTrackerFilters = useSelector((state) => state.filtersSlice.projectTracker);

  // Merge defaults into persisted filters so older persisted arrays (without the date filter) still work.
  const mergedFilters =
    projectTrackerFilters?.length > 0
      ? [
          ...projectTrackerFilters,
          ...projectTrackerFiltersArr.filter(
            (defaultFilter) =>
              !projectTrackerFilters.some(
                (persisted) => persisted.inputLabel === defaultFilter.inputLabel
              )
          ),
        ]
      : projectTrackerFiltersArr;

  // Default the date filter to today when no value is set (or is blank).
  const defaultTrackerDate = moment().format(defaultData.DATABSE_DATE_FORMAT);
  const initialFilters = mergedFilters.map((filter) =>
    filter.type === "date" && !filter.selectedValue
      ? { ...filter, selectedValue: defaultTrackerDate }
      : filter
  );

  const [filters, setFilters] = useState(initialFilters);
  const [locationData, setLocationData] = useState({ id: "", name: "" });
  const [loading, setLoading] = useState(false);
  const [viewLocationProgressAnchor, setViewLocationProgressAnchor] = useState({ right: false });
  const [isscopeReportDataExist, setIsscopeReportDataExist] = useState(false);
  const [reportHeaderFooterData, setReportHeaderFooterData] = useState(null);
  const [reportRowsData, setReportRowsData] = useState(null);
  const [totalCompletion, setTotalCompletion] = useState(0);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [tableHeight, setTableHeight] = useState("auto");
  const projectTrackerAbortRef = useRef(null);
  const latestProjectTrackerRequestRef = useRef(0);
  const open = Boolean(anchorEl);
  const selectedProjectId = filters[0]?.selectedValue;
  const selectedTrackerDate = filters.find((filter) => filter.type === "date")?.selectedValue || "";

  const updateTableHeight = useCallback(() => {
    if (typeof window === "undefined") return;

    const container = projectTrackerRef.current;
    if (!container) return;

    window.requestAnimationFrame(() => {
      const rect = container.getBoundingClientRect();
      const computedStyles = window.getComputedStyle(container);
      const paddingTop = parseFloat(computedStyles.paddingTop || "0");
      const paddingBottom = parseFloat(computedStyles.paddingBottom || "0");
      const marginBottom = parseFloat(computedStyles.marginBottom || "0");
      const borderBottomWidth = parseFloat(computedStyles.borderBottomWidth || "0");
      const topOffset = rect.top + paddingTop;
      const bottomOffset = paddingBottom + marginBottom + borderBottomWidth;
      const availableHeight = window.innerHeight - topOffset - bottomOffset;

      if (availableHeight > 0) {
        setTableHeight(`calc(100vh - ${Math.ceil(topOffset)}px - ${Math.ceil(bottomOffset)}px)`);
      } else {
        setTableHeight("auto");
      }
    });
  }, [projectTrackerRef]);

  const handleLocationClick = (locId, locName) => {
    setViewLocationProgressAnchor({ right: true });
    setLocationData({
      id: locId,
      name: locName,
    });
  };

  const cancelProjectTrackerRequest = useCallback(() => {
    if (projectTrackerAbortRef.current) {
      projectTrackerAbortRef.current.abort();
      projectTrackerAbortRef.current = null;
    }
  }, []);

  // Get Project List Func for dropdown.
  const getProjectListFunc = async () => {
    try {
      const projectList = await dispatch(projectListThunk());
      if (projectList?.payload?.status) {
        setFilters((prev) => {
          const updatedFilters = prev.map((filter) => {
            if (filter.inputLabel === FiltersModuleName.PROJECT) {
              return {
                ...filter,
                selectedValue: projectList?.payload?.data?.some(
                  (project) => project[Constants.MONGOOSE_ID] === filters[0]?.selectedValue
                )
                  ? filters[0]?.selectedValue
                  : FiltersModuleName.SELECT_IN_SMALL_CASE,
                list: [
                  FiltersModuleName.PROJECT_TRACKER_FILTERS_TITLE_OBJ,
                  ...projectList.payload.data,
                ],
              };
            }
            return filter;
          });

          // Updating the Store also with the latest Project List.
          dispatch(setStoreFilters({ module: "projectTracker", filters: updatedFilters }));
          return updatedFilters;
        });
      }
    } catch (error) {
      dispatch(
        openSnackbar({
          message: Constants.SOMETHING_WENT_WRONG,
          notificationType: Constants.NOTIFICATION_ERROR,
        })
      );
    }
  };

  const getProjectReportProgress = useCallback(
    async (id, trackerDate) => {
      cancelProjectTrackerRequest();

      const controller = new AbortController();
      const requestId = latestProjectTrackerRequestRef.current + 1;
      latestProjectTrackerRequestRef.current = requestId;
      projectTrackerAbortRef.current = controller;

      setLoading(true);

      try {
        const res = await dispatch(
          getProjectTrackerData({ projectId: id, trackerDate, signal: controller.signal })
        );

        if (
          controller.signal.aborted ||
          latestProjectTrackerRequestRef.current !== requestId ||
          res?.payload?.status !== 200
        ) {
          return;
        }

        const projectTrackerData = res?.payload?.data?.data?.projectTrackerData;
        const scopeData = res?.payload?.data?.data?.scopeData;
        const totalCompletions = res?.payload?.data?.data?.totalCompletions || 0;
        setReportHeaderFooterData(scopeData);
        setReportRowsData(projectTrackerData);
        setTotalCompletion(totalCompletions.toFixed(2));

        if (
          scopeData?.length === 0 ||
          (scopeData?.length === 1 && scopeData[0]?.reports?.length === 0)
        ) {
          setIsscopeReportDataExist(false);
        } else {
          setIsscopeReportDataExist(true);
        }
      } catch (error) {
        if (error?.name === "AbortError" || error?.code === "ERR_CANCELED") {
          return;
        }
      } finally {
        if (latestProjectTrackerRequestRef.current === requestId) {
          setLoading(false);
        }

        if (projectTrackerAbortRef.current === controller) {
          projectTrackerAbortRef.current = null;
        }
      }
    },
    [cancelProjectTrackerRequest, dispatch]
  );

  useEffect(() => {
    getProjectListFunc();
  }, []);

  // Effect to load data on component mount
  useEffect(() => {
    if (selectedProjectId && selectedProjectId !== FiltersModuleName.SELECT_IN_SMALL_CASE) {
      getProjectReportProgress(selectedProjectId, selectedTrackerDate);
      return;
    }

    cancelProjectTrackerRequest();
    latestProjectTrackerRequestRef.current += 1;
    setLoading(false);
    setReportHeaderFooterData(null);
    setReportRowsData(null);
    setTotalCompletion(0);
    setIsscopeReportDataExist(false);
  }, [
    cancelProjectTrackerRequest,
    getProjectReportProgress,
    selectedProjectId,
    selectedTrackerDate,
  ]);

  useEffect(
    () => () => {
      cancelProjectTrackerRequest();
    },
    [cancelProjectTrackerRequest]
  );

  const handleReload = async () => {
    await getProjectListFunc();
    if (selectedProjectId && selectedProjectId !== FiltersModuleName.SELECT_IN_SMALL_CASE) {
      await getProjectReportProgress(selectedProjectId, selectedTrackerDate);
    }
  };

  const handleFilterChange = async (e) => {
    const { value } = e.target;
    const selectedProject = value || FiltersModuleName.SELECT_IN_SMALL_CASE;

    setFilters((prev) => {
      const currentProject = prev.find(
        (filter) => filter.inputLabel === FiltersModuleName.PROJECT
      )?.selectedValue;
      const hasProjectChanged = currentProject !== selectedProject;
      const updatedFilters = prev.map((filter) => {
        if (filter.inputLabel === FiltersModuleName.PROJECT) {
          return {
            ...filter,
            selectedValue: selectedProject,
          };
        }
        if (filter.type === "date" && hasProjectChanged) {
          return {
            ...filter,
            selectedValue: moment().format(defaultData.DATABSE_DATE_FORMAT),
          };
        }
        return filter;
      });

      // Updating the Store also with the latest Project id value.
      dispatch(setStoreFilters({ module: "projectTracker", filters: updatedFilters }));
      return updatedFilters;
    });
  };

  const handleDateFilterChange = (date) => {
    const formattedDate = date ? moment(date).format(defaultData.DATABSE_DATE_FORMAT) : "";

    setFilters((prev) => {
      const updatedFilters = prev.map((filter) => {
        if (filter.type === "date") {
          return {
            ...filter,
            selectedValue: formattedDate,
          };
        }
        return filter;
      });

      // Updating the Store also with the latest date value.
      dispatch(setStoreFilters({ module: "projectTracker", filters: updatedFilters }));
      return updatedFilters;
    });
  };

  const handleResetFilter = () => {
    cancelProjectTrackerRequest();
    latestProjectTrackerRequestRef.current += 1;
    setLoading(false);

    setFilters((prev) => {
      const updatedFilters = prev.map((filter) => {
        if (filter.inputLabel === FiltersModuleName.PROJECT) {
          return {
            ...filter,
            selectedValue: FiltersModuleName.SELECT_IN_SMALL_CASE,
          };
        }
        if (filter.type === "date") {
          return {
            ...filter,
            selectedValue: "",
          };
        }
        return filter;
      });

      // Updating the Store also with the latest Project id value.
      dispatch(resetFilters({ module: "projectTracker" }));
      return updatedFilters;
    });
  };

  // Helper function to load images
  const loadImage = (url) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = url;
    });

  const handleDownloadPdf = async (fileName) => {
    try {
      const input = document.getElementById("pdfTable");

      if (!input) {
        await dispatch(
          openSnackbar({ message: Constants.SOMETHING_WENT_WRONG, notificationType: "error" })
        );
        return;
      }

      const pdf = new JSPDF("landscape", "pt", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const headerHeight = 40;
      const footerHeight = 30;
      const usableWidth = pageWidth - margin * 2;
      const tableTop = headerHeight + margin + 35;
      const tableBottomPadding = 10;
      const usableHeight = pageHeight - footerHeight - tableTop - tableBottomPadding;
      const rowHeight = 40;

      const rows = Array.from(input.querySelectorAll("tr"));
      const totalColumns = input.querySelectorAll("th").length;
      const theadRows = Array.from(input.querySelectorAll("thead tr"));
      const theadRowCount = theadRows.length;
      // A body row is a "group start" only when it contains the location cell,
      // which uniquely renders the .eye-icon-button. Continuation rows (assetIndex > 0)
      // omit the location cell because it's covered by the parent rowspan, so splitting
      // a page mid-group drops the Location column and shifts every later column left.
      const isGroupStartRow = (idx) =>
        idx >= theadRowCount && rows[idx]?.querySelector?.(".eye-icon-button") !== null;

      const setTableStyles = (table) => {
        const tableCopy = table;
        tableCopy.style.width = `${usableWidth}px`;
        tableCopy.style.borderCollapse = "separate";
        tableCopy.style.borderSpacing = "0";
        tableCopy.style.borderRadius = "4px";
        tableCopy.style.overflow = "hidden";

        // Hide all elements with the 'eye-icon-button' class
        Array.from(tableCopy.querySelectorAll(".eye-icon-button")).forEach((icon) => {
          const item = icon;
          item.style.display = "none";
        });

        Array.from(tableCopy.querySelectorAll("th, td")).forEach((cell) => {
          const cellCopy = cell;
          cellCopy.style.border = "1px solid #E0E6F5";
          cellCopy.style.padding = "5px";
          cellCopy.style.boxSizing = "border-box";
          cellCopy.style.wordWrap = "break-word";
          cellCopy.style.overflow = "hidden";
          cellCopy.style.height = `${rowHeight}px`;
          cellCopy.style.width = `${usableWidth / totalColumns}px`;
        });
      };

      const createTableChunk = (chunkStart, chunkEnd) => {
        const chunk = document.createElement("table");
        const tbody = document.createElement("tbody");

        if (chunkStart !== 0) {
          chunk.appendChild(input.querySelector("thead").cloneNode(true));
        }

        for (let i = chunkStart; i < chunkEnd; i += 1) {
          tbody.appendChild(rows[i].cloneNode(true));
        }

        chunk.appendChild(tbody);
        setTableStyles(chunk);
        return chunk;
      };

      const measureTableChunkHeight = (chunkStart, chunkEnd) => {
        const chunk = createTableChunk(chunkStart, chunkEnd);
        const measurementContainer = document.createElement("div");

        measurementContainer.style.position = "absolute";
        measurementContainer.style.top = "0";
        measurementContainer.style.left = "-9999px";
        measurementContainer.style.visibility = "hidden";
        measurementContainer.style.width = `${usableWidth}px`;
        measurementContainer.appendChild(chunk);
        document.body.appendChild(measurementContainer);

        const { height } = chunk.getBoundingClientRect();
        measurementContainer.remove();
        return height;
      };

      const generatePdfChunk = async (chunkHtml, pageNumber, totalPages) => {
        const chunkDiv = document.createElement("div");
        chunkDiv.innerHTML = chunkHtml;
        chunkDiv.style.position = "absolute";
        chunkDiv.style.top = "-9999px";
        chunkDiv.style.left = "-9999px";
        document.body.appendChild(chunkDiv);

        try {
          const canvas = await html2canvas(chunkDiv, { scale: 1, useCORS: true });
          const imgData = canvas.toDataURL("image/png");
          const imgWidth = usableWidth;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;

          // Add header image
          const headerImgUrl =
            "https://reynard-dev-82a6381.sfo3.cdn.digitaloceanspaces.com/images/Mail_Images/reynard_logo_pdf.png";
          const headerImg = await loadImage(headerImgUrl);

          const headerImgHeight = 35;
          const headerImgWidth = (headerImgHeight / 20) * 80;

          pdf.addImage(headerImg, "PNG", margin, margin, headerImgWidth, headerImgHeight);

          // Add title and date on the first page only
          if (pageNumber === 1) {
            const title = "Project Tracker";
            const todayDate = moment(selectedTrackerDate || new Date()).format(
              defaultData.WEB_DATE_FORMAT
            );

            pdf.setFontSize(16);
            pdf.setTextColor("#191A51");
            pdf.text(title, margin, headerHeight + margin + 20, { align: "left" });

            pdf.setFontSize(12);
            pdf.setTextColor("#000000");
            pdf.text(
              `(${todayDate})`,
              margin + pdf.getStringUnitWidth(title) * 14 + 18,
              headerHeight + margin + 18,
              { align: "left" }
            );
          }

          // Add the table chunk as an image
          pdf.addImage(imgData, "PNG", margin, tableTop, imgWidth, imgHeight);

          // Add footer on each page
          const footerImgUrl =
            "https://reynard-dev-82a6381.sfo3.cdn.digitaloceanspaces.com/images/Mail_Images/reynard_logo_pdf.png";
          const footerImg = await loadImage(footerImgUrl);

          // Add "Powered by:" text and logo image
          pdf.setTextColor(84, 83, 83);
          pdf.setFontSize(12);
          pdf.text("Powered by:", margin, pageHeight - footerHeight + 15);

          pdf.addImage(footerImg, "PNG", margin + 70, pageHeight - footerHeight + 2, 80, 20);

          // Add page number
          pdf.text(
            `Page ${pageNumber} of ${totalPages}`,
            pageWidth - margin,
            pageHeight - footerHeight + 15,
            { align: "right" }
          );
        } catch (error) {
          await dispatch(
            openSnackbar({ message: Constants.SOMETHING_WENT_WRONG, notificationType: "error" })
          );
        } finally {
          document.body.removeChild(chunkDiv); // Clean up
        }
      };

      const planChunkEnd = (chunkStart, totalRows) => {
        let cursor = chunkStart;
        while (
          cursor < totalRows &&
          measureTableChunkHeight(chunkStart, cursor + 1) <= usableHeight
        ) {
          cursor += 1;
        }

        // A single oversized row must still be emitted rather than stopping pagination.
        if (cursor === chunkStart) return Math.min(chunkStart + 1, totalRows);
        if (cursor >= totalRows) return cursor;

        // If we would stop mid-group, back the cursor up to the most recent group-start row
        // within this chunk so the next chunk begins with its Location cell intact.
        if (!isGroupStartRow(cursor)) {
          const firstDataRow = chunkStart === 0 ? theadRowCount : chunkStart;
          let back = cursor - 1;
          while (back > chunkStart && !isGroupStartRow(back)) back -= 1;
          if (back > firstDataRow && isGroupStartRow(back)) cursor = back;
        }
        return cursor;
      };

      const processChunks = async () => {
        const totalRows = rows.length;

        // Pre-compute page boundaries so totalPages is accurate for the footer label.
        const boundaries = [];
        let planCursor = 0;
        while (planCursor < totalRows) {
          const end = planChunkEnd(planCursor, totalRows);
          if (end <= planCursor) break;
          boundaries.push([planCursor, end]);
          planCursor = end;
        }
        const totalPages = boundaries.length || 1;

        for (let pageIdx = 0; pageIdx < boundaries.length; pageIdx += 1) {
          const [chunkStart, chunkEnd] = boundaries[pageIdx];
          const chunk = createTableChunk(chunkStart, chunkEnd);

          // eslint-disable-next-line no-await-in-loop
          await generatePdfChunk(chunk.outerHTML, pageIdx + 1, totalPages);

          if (pageIdx < boundaries.length - 1) {
            pdf.addPage();
          }
        }

        // Save PDF
        pdf.save(fileName);
      };

      try {
        await processChunks();
      } catch (error) {
        await dispatch(
          openSnackbar({ message: Constants.SOMETHING_WENT_WRONG, notificationType: "error" })
        );
      }
    } catch (err) {
      await dispatch(
        openSnackbar({ message: Constants.SOMETHING_WENT_WRONG, notificationType: "error" })
      );
    }
  };

  const handleExportProjectTrackerPdf = async (format) => {
    setAnchorEl(null);
    setPdfLoading(true);
    const fileName = `Reynard_Project_Tracker(${
      filters[0].list.find(
        (item) => item[Constants.MONGOOSE_ID] === filters[0]?.selectedValue.replace(/ /g, "_")
      )?.title || ""
    }).${format}`;
    if (format === "pdf") {
      await handleDownloadPdf(fileName);
    } else {
      try {
        const browserDateTime = selectedTrackerDate
          ? moment(selectedTrackerDate).format(defaultData.WEB_24_HOURS_FORMAT)
          : moment().format(defaultData.WEB_24_HOURS_FORMAT);
        const res = await dispatch(
          exportProjectTrackerExcel({
            projectId: filters[0].selectedValue,
            browserDateTime,
            trackerDate: selectedTrackerDate,
          })
        );
        const url = window.URL.createObjectURL(res.payload);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (err) {
        await dispatch(
          openSnackbar({ message: Constants.SOMETHING_WENT_WRONG, notificationType: "error" })
        );
      }
    }
    setPdfLoading(false);
  };

  const handleOpenExportMenu = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleCloseExportMenu = () => {
    setAnchorEl(null);
  };

  const { columns, rows, extraHeadersList, footerList } = reportCalculationData(
    reportHeaderFooterData,
    reportRowsData,
    totalCompletion,
    handleLocationClick
  );

  useEffect(() => {
    if (selectedProjectId === FiltersModuleName.SELECT_IN_SMALL_CASE || !isscopeReportDataExist) {
      setTableHeight("auto");
      return;
    }
    updateTableHeight();
  }, [
    selectedProjectId,
    isscopeReportDataExist,
    loading,
    reportRowsData,
    extraHeadersList,
    footerList,
    updateTableHeight,
  ]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleResize = () => {
      updateTableHeight();
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [updateTableHeight]);

  const renderContent = () => {
    if (loading) {
      return (
        <MDBox
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          {Icons.LOADING2}
        </MDBox>
      );
    }
    if (filters[0]?.selectedValue === "select" || !isscopeReportDataExist) {
      return (
        <Card sx={{ height: "60vh", marginTop: 3 }}>
          <MDBox display="flex" justifyContent="center" alignItems="center" height="100%">
            <MDTypography sx={{ fontSize: "25px", fontWeight: "semibold", opacity: "0.8" }}>
              {filters[0]?.selectedValue === "select"
                ? "Please Select Project"
                : "Nothing to track for selected project"}
            </MDTypography>
          </MDBox>
        </Card>
      );
    }
    return (
      <MDBox
        sx={{
          paddingTop: pxToRem(24),
          paddingBottom: pxToRem(32),
        }}
        ref={projectTrackerRef}
        id="pdfTable"
      >
        <ProjectTrackerTable
          table={{ columns, rows }}
          isSorted={false}
          loading={Constants.FULFILLED}
          extraHeaders={extraHeadersList}
          footerList={footerList}
          containerHeight={tableHeight}
        />
      </MDBox>
    );
  };

  return (
    <>
      <DashboardLayout module={defaultData.TRACKERS_SCREEN_ID}>
        <DashboardNavbar />
        <MDBox display="flex" justifyContent="space-between">
          <PageTitle title={PageTitles.REPORT_CALCULATION} />
          <BasicButton
            icon={Icons.RELOAD}
            background={Colors.WHITE}
            border
            color={Colors.BLACK}
            action={handleReload}
            disabled={filters[0]?.selectedValue === "select"}
          />
        </MDBox>
        <Divider sx={{ marginTop: 1 }} />
        <Feature name={FeatureTags.PROJECT_TRACKER}>
          <>
            <MDBox display="flex" justifyContent="space-between" mt={0} mx={0} width="100%">
              <MDBox
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "start",
                  alignItems: "flex-end",
                }}
                mt={0}
                mx={0}
                width="100%"
              >
                {filters?.map((val) =>
                  val?.type === "date" ? (
                    <FormControl
                      key={val?.inputLabel}
                      variant="standard"
                      size="medium"
                      style={{ marginTop: "26px", width: 200, marginRight: "15px" }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: Colors.WHITE,
                          height: pxToRem(40),
                          padding: pxToRem(1),
                        },
                        "& .MuiInputBase-input": {
                          color: "#344054",
                          "&::placeholder": {
                            color: "#667085",
                            opacity: 1,
                          },
                        },
                      }}
                    >
                      <MDTypography
                        variant="caption"
                        mb={-1}
                        sx={{
                          fontSize: pxToRem(14),
                          fontWeight: 600,
                          color: "#344054",
                        }}
                      >
                        {val?.inputLabel}
                      </MDTypography>
                      <FTextField
                        name={val?.inputLabel}
                        type="date"
                        value={val?.selectedValue || ""}
                        handleChange={(e) => handleDateFilterChange(e.target.value)}
                        InputProps={{
                          inputProps: {
                            max: moment().format(defaultData.DATABSE_DATE_FORMAT),
                            style: { textTransform: "uppercase" },
                          },
                          readOnly: false,
                          style: {
                            height: "40px",
                          },
                        }}
                        onKeyPress={(e) => {
                          e.preventDefault();
                        }}
                      />
                    </FormControl>
                  ) : (
                    <FormControl
                      key={val?.inputLabel}
                      variant="standard"
                      size="medium"
                      style={{ marginTop: "26px", width: 200, marginRight: "15px" }}
                    >
                      <CustomAutoComplete
                        label={val?.inputLabel}
                        name={val?.inputLabel}
                        id={val?.inputLabel}
                        getOptionLabel={(option) => option.title || ""}
                        menu={val?.list}
                        value={{
                          title:
                            val?.list.find(
                              (item) => item[Constants.MONGOOSE_ID] === val?.selectedValue
                            )?.title || "",
                        }}
                        handleChange={handleFilterChange}
                        valueStyle={{
                          backgroundColor: Colors.WHITE,
                          color: "#344054",
                          fontWeight: 500,
                          height: pxToRem(40),
                          verticalMarginTop: pxToRem(4),
                          menuWidth: 400,
                          inputWidth: 250,
                          padding: pxToRem(1),
                        }}
                        labelStyle={{
                          fontSize: pxToRem(14),
                          fontWeight: 600,
                          color: Colors.BLACK,
                        }}
                        textTransform={val.textTransform}
                      />
                    </FormControl>
                  )
                )}
                <ResetFilterButton handleReset={handleResetFilter} style={{ marginLeft: "1rem" }} />
              </MDBox>
              <MDBox
                sx={{
                  display: "flex",
                  alignItems: "end",
                  flexWrap: "wrap",
                }}
              >
                <Button
                  sx={{
                    m: 2,
                    mt: pxToRem(45),
                    ml: 0,
                    mb: 0,
                    backgroundColor: "#fff",
                    "&:hover": {
                      backgroundColor: "#fff",
                    },
                    fontSize: pxToRem(14),
                    textTransform: "capitalize",
                    width: pxToRem(130),
                  }}
                  variant="outlined"
                  color="info"
                  onClick={handleOpenExportMenu}
                  startIcon={Icons.EXPORT}
                  disabled={
                    filters[0]?.selectedValue === "select" || pdfLoading || !isscopeReportDataExist
                  }
                >
                  {pdfLoading ? ButtonTitles.EXPORTING : ButtonTitles.EXPORT}
                </Button>
              </MDBox>
              <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleCloseExportMenu}
                MenuListProps={{
                  "aria-labelledby": "basic-button",
                }}
              >
                <MenuItem onClick={() => handleExportProjectTrackerPdf("xlsx")}>CSV</MenuItem>
                <MenuItem onClick={() => handleExportProjectTrackerPdf("pdf")}>PDF</MenuItem>
              </Menu>
            </MDBox>
            {renderContent()}
          </>
        </Feature>
      </DashboardLayout>
      {viewLocationProgressAnchor.right && (
        <LocationCalculation
          viewLocationProgressAnchor={viewLocationProgressAnchor}
          setViewLocationProgressAnchor={setViewLocationProgressAnchor}
          locationData={locationData}
          setLocationData={setLocationData}
          trackerDate={selectedTrackerDate}
        />
      )}
    </>
  );
};

export default ReportCalculation;
